import React from 'react';
import { ShieldCheck, ShieldAlert } from 'lucide-react';

/**
 * <ApprovedBadge cert={cert} /> — premium tricolour seal: black + gold + section colour.
 *
 * cert: { section, status }   where status ∈ approved | at_risk | suspended | eligible_pending | not_eligible
 *
 * Only "approved" and "at_risk" are rendered (others return null) — these are the
 * statuses where the installer is still publicly "an Approved Installer".
 */
const SECTION_LABELS = {
  probound: 'ProBound',
  proepoxy: 'ProEpoxy',
  prodecor: 'ProDecor',
};

// Each section gets its accent colour. Black + gold are the common Ox Approved seal colours.
const SECTION_ACCENT = {
  probound: '#2F5E96', // ProBound blue
  proepoxy: '#7A0B7A', // ProEpoxy purple
  prodecor: '#D4A24B', // ProDecor amber (already gold-ish; we use a slightly darker brown to keep gold distinct)
};

const GOLD = '#D4A24B';
const BLACK = '#0B0B0E';

export function ApprovedBadge({ cert, size = 'sm' }) {
  if (!cert) return null;
  if (cert.status !== 'approved' && cert.status !== 'at_risk') return null;

  const accent = SECTION_ACCENT[cert.section] || '#2F5E96';
  const label = SECTION_LABELS[cert.section] || cert.section;
  const Icon = cert.status === 'at_risk' ? ShieldAlert : ShieldCheck;
  const isAtRisk = cert.status === 'at_risk';

  const sizes = {
    sm: { pad: 'px-1.5 py-0.5', text: 'text-[10px]', icon: 'w-3 h-3', sep: 'mx-1' },
    md: { pad: 'px-2 py-0.5', text: 'text-[11px]', icon: 'w-3.5 h-3.5', sep: 'mx-1' },
    lg: { pad: 'px-2.5 py-1', text: 'text-xs', icon: 'w-4 h-4', sep: 'mx-1.5' },
  };
  const s = sizes[size] || sizes.sm;

  // Tricolour seal:
  //   - Black background (the OX side)
  //   - Gold border + gold "OX APPROVED" text + gold shield
  //   - Section-coloured "stripe" on the right with the section name
  return (
    <span
      data-testid={`approved-badge-${cert.section}-${cert.status}`}
      className={`inline-flex items-center rounded-full font-bold uppercase tracking-wider overflow-hidden shadow-sm ${s.text}`}
      style={{
        background: BLACK,
        border: `1.5px solid ${GOLD}`,
        opacity: isAtRisk ? 0.78 : 1,
      }}
      title={isAtRisk ? `${label} approved (at-risk)` : `${label} Ox Approved`}
    >
      {/* Left half: gold OX APPROVED on black */}
      <span className={`inline-flex items-center gap-1 ${s.pad}`} style={{ color: GOLD }}>
        <Icon className={s.icon} />
        <span>Ox Approved</span>
      </span>

      {/* Section stripe — uses the section accent */}
      <span
        className={s.pad}
        style={{
          background: accent,
          color: cert.section === 'prodecor' ? BLACK : '#fff',
          borderLeft: `1.5px solid ${GOLD}`,
        }}
      >
        {label}
      </span>
    </span>
  );
}

export function ApprovedBadgeList({ certs, size = 'sm' }) {
  const visible = (certs || []).filter(c => c.status === 'approved' || c.status === 'at_risk');
  if (visible.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap gap-1" data-testid="approved-badge-list">
      {visible.map(c => <ApprovedBadge key={c.section} cert={c} size={size} />)}
    </span>
  );
}

export default ApprovedBadge;
